// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import type { ApexOptions } from "apexcharts"
import type { ReferenceCostLine, ReferenceSeries } from "./derive"

/**
 * Shared ApexCharts y-axis annotations for the reference overlay — the
 * dashed reference level line on the fitness box-plot and the cost tab.
 * Centralised here so both charts draw the reference with one styling
 * source: the accent-colored dashed line with a bare right-anchored
 * label, per the binding Claude Design mockup (variant 1b). The accent
 * is shared with the Reference card (`--reference` token) so the line
 * and the card read as one object.
 */

type YAxisAnnotation = NonNullable<
  NonNullable<ApexOptions["annotations"]>["yaxis"]
>[number]

/** Mirrors the `--reference` CSS token — SVG attrs don't resolve vars. */
export const REFERENCE_ACCENT = "#9d7bff"

/**
 * A dashed accent level line at `y`, `text` right-anchored. The label
 * sits below the line (mockup 1b) unless `labelAbove` — used by the cost
 * tab, whose level typically hugs the bottom of the plot area (a below
 * label would clip into the x-axis).
 */
export const referenceLineAnnotation = (
  y: number,
  text: string,
  { labelAbove = false }: { labelAbove?: boolean } = {},
): YAxisAnnotation => ({
  y,
  strokeDashArray: 6,
  borderColor: REFERENCE_ACCENT,
  borderWidth: 1.5,
  label: {
    text,
    position: "right",
    textAnchor: "end",
    offsetY: labelAbove ? -4 : 20,
    borderColor: "transparent",
    borderWidth: 0,
    style: {
      color: REFERENCE_ACCENT,
      background: "transparent",
      fontSize: "12px",
      fontWeight: 600,
    },
  },
})

/**
 * Fitness box-plot y-axis annotation for the reference overlay: a single
 * dashed level line at the reference MAX, labeled `reference` (mockup 1b
 * — N stays disclosed in the adjacent Reference card and in the chart's
 * accessible caption). A *level*, not an iteration — it never enters the
 * box-plot series or shifts the X-axis (INV-2).
 */
export const referenceFitnessAnnotations = (
  ref: ReferenceSeries,
): ApexOptions["annotations"] => ({
  yaxis: [referenceLineAnnotation(ref.max, "reference")],
})

/**
 * Cost-tab y-axis annotation for the reference overlay: a single dashed
 * level line at the references' cost/run, labeled with the pre-formatted
 * `$` figure (no score/€ normalization — INV-8).
 */
export const referenceCostAnnotations = (
  line: ReferenceCostLine,
): ApexOptions["annotations"] => ({
  yaxis: [
    referenceLineAnnotation(
      line.costPerRun,
      `reference cost/run ${line.formatted}`,
      { labelAbove: true },
    ),
  ],
})
